/* RESPONSIBLE TEAM: team-omni */
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import { request } from 'embercom/lib/inbox/requests';

export interface SlackUser {
  id: string;
  real_name: string;
  avatar_url: string;
  name: string;
  in_channel: boolean;
  type: 'user' | 'usergroup';
}

interface SlackUsersCache {
  users: SlackUser[];
  isPrivateChannel: boolean;
  timestamp: number;
}

let CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export default class SlackUsersService extends Service {
  @service declare session: Session;

  @tracked isLoading = false;
  @tracked error: Error | null = null;

  private cache: Map<string, SlackUsersCache> = new Map();

  /**
   * Fetches Slack channel members for a given channel ID
   * @param channelId - The Slack channel database ID
   * @param forceRefresh - Force a fresh fetch, bypassing cache
   * @returns Array of Slack users
   */
  async fetchSlackChannelMembers(channelId: string, forceRefresh = false): Promise<SlackUser[]> {
    if (!channelId) {
      return [];
    }

    if (!forceRefresh) {
      let cached = this.cache.get(channelId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
        return cached.users;
      }
    }

    this.isLoading = true;
    this.error = null;

    try {
      let response = await request(
        `/ember/slack/v2/channels/${channelId}/fetch_members?app_id=${this.session.workspace.id}`,
      );

      if (response.status === 403 || response.status === 401) {
        console.warn(
          '[SlackUsersService] Permission denied - admin lacks required permissions for Slack channel members',
        );
        this.error = new Error('Permission denied');
        this.cache.set(channelId, {
          users: [],
          isPrivateChannel: false,
          timestamp: Date.now(),
        });
        return [];
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let data = (await response.json()) as { users: SlackUser[]; is_private: boolean };

      let users = data.users || [];

      this.cache.set(channelId, {
        users,
        isPrivateChannel: data.is_private,
        timestamp: Date.now(),
      });

      return users;
    } catch (error) {
      console.error('[SlackUsersService] Error fetching Slack channel members:', error);
      this.error = error instanceof Error ? error : new Error('Unknown error');

      let cached = this.cache.get(channelId);
      return cached?.users || [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Filters Slack users by search query
   * @param users - Array of Slack users
   * @param query - Search query string
   * @returns Filtered array of Slack users
   */
  filterUsers(users: SlackUser[], query: string): SlackUser[] {
    if (!query) {
      return users;
    }

    let lowerQuery = query.toLowerCase();
    return users.filter((user) => {
      let realName = user.real_name?.toLowerCase() || '';
      let name = user.name?.toLowerCase() || '';

      return realName.includes(lowerQuery) || name.includes(lowerQuery);
    });
  }

  isPrivateChannel(channelId: string | null): boolean {
    if (!channelId) {
      return false;
    }
    let cached = this.cache.get(channelId);
    return cached?.isPrivateChannel || false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'slack-users-service': SlackUsersService;
  }
}
